/**
 * prepareDriverDocument
 *
 * Reads a File, enforces a 5 MB raw-byte cap, and returns the file content as
 * a raw base64 string (no "data:" prefix) plus its MIME type.
 *
 * Images over the cap are shrunk via a canvas downscale loop.
 * PDFs and any other non-image type over the cap throw FileTooLargeError.
 */

export class FileTooLargeError extends Error {
  constructor(message = "File is too large. Please upload a file under 5 MB.") {
    super(message);
    this.name = "FileTooLargeError";
    // Fix prototype chain for instanceof checks in transpiled code.
    Object.setPrototypeOf(this, FileTooLargeError.prototype);
  }
}

const SIZE_CAP_BYTES = 5 * 1024 * 1024; // 5 MB

// ---------------------------------------------------------------------------
// Injection point for the canvas downscale step.
//
// jsdom does not implement a real canvas, so tests that exercise the
// over-cap image path inject a stub here instead of a real canvas.
// Production code leaves `_downscaleImage.fn` null (falsy) and the real
// canvas path (`_realDownscaleImage`) is used.
//
// Usage in tests:
//   _downscaleImage.fn = jest.fn().mockResolvedValue({ base64: tinyBase64, contentType: "image/jpeg" }); // or null
// ---------------------------------------------------------------------------
export const _downscaleImage: {
  fn:
    | ((
        base64: string,
        contentType: string
      ) => Promise<{ base64: string; contentType: string } | null>)
    | null;
} = { fn: null };

/**
 * Real downscale implementation: re-encodes the image onto a canvas at
 * decreasing quality/scale until the raw byte count is at or below the cap,
 * or returns null if it cannot get under the cap.
 *
 * Returns the ACTUAL content type it encoded to (image/jpeg for PNG inputs,
 * since canvas re-encodes PNG→JPEG for size reduction) so callers can store
 * the correct content_type alongside the bytes.
 */
async function _realDownscaleImage(
  base64: string,
  contentType: string
): Promise<{ base64: string; contentType: string } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }

      let scale = 1.0;
      let quality = 0.9;
      const encodeType =
        contentType === "image/png" ? "image/jpeg" : contentType;

      const tryEncode = (): string | null => {
        const w = Math.max(1, Math.floor(img.naturalWidth * scale));
        const h = Math.max(1, Math.floor(img.naturalHeight * scale));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL(encodeType, quality);
        // dataUrl = "data:<type>;base64,<b64>"
        const b64 = dataUrl.split(",")[1] ?? "";
        const byteLen = Math.floor((b64.length * 3) / 4);
        return byteLen <= SIZE_CAP_BYTES ? b64 : null;
      };

      // Try progressively lower quality / scale until under cap
      const qualities = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
      const scales = [1.0, 0.75, 0.5, 0.35, 0.25];

      for (const s of scales) {
        scale = s;
        for (const q of qualities) {
          quality = q;
          const result = tryEncode();
          if (result !== null) {
            resolve({ base64: result, contentType: encodeType });
            return;
          }
        }
      }

      resolve(null); // could not shrink below cap
    };
    img.onerror = () => resolve(null);
    img.src = `data:${contentType};base64,${base64}`;
  });
}

/**
 * Read a File into a raw base64 string (no data: prefix) via FileReader.
 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:<type>;base64,<b64>" — strip the prefix
      const commaIdx = result.indexOf(",");
      if (commaIdx === -1) {
        reject(new Error("FileReader produced an unexpected result format"));
        return;
      }
      resolve(result.slice(commaIdx + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Returns the raw byte count of a base64 string (without a data: prefix).
 * Accounts for trailing "=" padding.
 */
function base64ByteLength(b64: string): number {
  const padding = (b64.match(/={1,2}$/) ?? [""])[0].length;
  return Math.floor((b64.length * 3) / 4) - padding;
}

/**
 * Prepare a driver document file for upload.
 *
 * @param file - the File selected by the user
 * @returns `{ base64, contentType }` where `base64` has NO "data:" prefix
 * @throws FileTooLargeError if the file exceeds 5 MB and cannot be downscaled
 */
export async function prepareDriverDocument(
  file: File
): Promise<{ base64: string; contentType: string }> {
  const contentType = file.type;
  const isImage = contentType.startsWith("image/");

  const base64 = await readFileAsBase64(file);
  const rawBytes = base64ByteLength(base64);

  if (rawBytes <= SIZE_CAP_BYTES) {
    return { base64, contentType };
  }

  // Over cap ----------------------------------------------------------------

  if (!isImage) {
    throw new FileTooLargeError();
  }

  // Try to downscale the image
  const downscale = _downscaleImage.fn ?? _realDownscaleImage;
  const shrunken = await downscale(base64, contentType);

  if (shrunken === null) {
    throw new FileTooLargeError();
  }

  // Use the content type the downscaler actually encoded to (may differ from
  // the original — e.g. PNG input is re-encoded as JPEG by the canvas path).
  return { base64: shrunken.base64, contentType: shrunken.contentType };
}
